import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { postInventorySnapshot, type ISnapshotBody } from '@apis/logistics';
import { isStaleRefusal } from '@apis/logisticsRevision';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { useLogisticsPageToast } from '../LogisticsToastHost';
import { failureToast } from '../logisticsFailureToast';
import type { useStockAreaAssignment } from './useStockAreaAssignment';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

interface IUseStockEntrySaveArgs {
  siteId: string;
  area: ReturnType<typeof useStockAreaAssignment>;
  buildBody: () => ISnapshotBody;
  /** The material the area move files. */
  query: string;
  push: (toast: Omit<IToast, 'id'>) => void;
  onClose: () => void;
}

/**
 * The stock form's save — extracted from `StockEntryForm` to keep that file
 * under the size gate. Sends the Logistics page's revision (from the edit-lock
 * context) with the snapshot.
 */
export function useStockEntrySave({ siteId, area, buildBody, query, push, onClose }: IUseStockEntrySaveArgs) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { revision } = useLogisticsEditLock();
  /* The area-move notice is raised just before the form closes, so it goes to
     the page's stack when there is one — the form's own unmounts with it. */
  const notice = useLogisticsPageToast() ?? push;
  /* Refetched whether or not the save landed — a refusal may still be news
     (someone else's write is why it was refused). */
  const refetch = () => {
    void qc.invalidateQueries({ queryKey: ['logistics', siteId] });
    void qc.invalidateQueries({ queryKey: ['site-map', siteId] });
  };
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: async () => {
      // An arrival this count reconciles rides in the body (`arrivals`) — the
      // server checks, arrives, then counts, in one request.
      const saved = await postInventorySnapshot(siteId, buildBody(), revision);
      /* The area move runs AFTER the snapshot lands (the stock write is the
         record; the map placement annotates it) and NEVER rejects — see
         `useStockAreaAssignment.commit`: a failed move must not report the
         whole save as failed while the snapshot is already persisted. */
      const areaMoved = await area.commit(query);
      return { saved, areaMoved };
    },
    onSuccess: ({ areaMoved }) => {
      /* The record saved either way, so the form closes either way — but a
         failed area move gets its own message rather than hiding behind a
         "couldn't save" that would invite a duplicate-snapshot retry. */
      if (!areaMoved) notice({ kind: 'error', title: t('logistics.stockDialog.areaMoveError') });
      onClose();
    },
    // A stale refusal is the refresh dock's to announce, and the form stays open
    // with its input.
    onError: (error) => {
      if (!isStaleRefusal(error)) push(failureToast(t, error, t('logistics.stockDialog.error')));
    },
    onSettled: refetch,
  });
}
